import instance from './axios/index';
import { API_BASE_URL } from '../configs/AppConfig';
import { normalData } from '../utils/normal-data';
import { pingServer } from 'components/shared-components/Notification/GeneralNotification';

class DataService {
    constructor(modelName, apiBaseURL = API_BASE_URL) {
        this.modelName = modelName;

        this.fileTypes = {};
        this.fileTypes.pdf = 'application/pdf';
        this.fileTypes.xlsx = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        this.fileTypes.docx = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        this.fileTypes.csv = 'text/csv;charset=utf-8';
        this.fileTypes.xml = 'text/xml; charset=utf-8';
        this.fileTypes.json = 'text/json; charset=utf-8';
        this.apiBaseURL = apiBaseURL;
    }

    async getAll(request = {}) {
        /*
      - Hàm này cho phép lấy tất cả các bản ghi có trong bảng.
      - Các tham số:
        + toplist: Danh sách các mã định danh của các bản ghi cần xuất hiện trên đầu danh sách
        + idlist: Danh sách các mã định danh của các bản ghi cần lấy.
        + columnlist: Danh sách các cột muốn lấy thông tin. Nếu không có sẽ lấy tất cả các cột
      -Dữ liệu trả về:
        + data: Danh sách các bản ghi lấy được
        + count: Số lượng các bản ghi 
    */

        try {
            let param = [];

            if (request.toplist != null) {
                param.push(`toplist=${request.toplist.join(',')}`);
            }

            if (request.columnlist != null) {
                param.push(`columnlist=${request.columnlist.join(',')}`);
            }

            if (request.query != null) param.push(`query=${request.query}`);

            if (request.sortBy != null && request.sortBy.length > 0) {
                param.push(`order=${request.sortBy}`);
            }

            if (request.idlist != null && request.idlist.length > 0) {
                param.push(`idlist=${request.idlist.join(',')}`);
            }

            let strParam = param.join('&');
            let url = `${this.apiBaseURL}${this.modelName}/?${strParam}`;

            const responseData = await instance.get(url, {
                headers: { 'Content-Type': 'application/json' },
            });

            let data = normalData(responseData.data.data);
            let message = responseData.data.data.message;
            let count = data ? data.length : 0;

            return Promise.resolve({
                data,
                count,
                message,
            });
        } catch (error) {
            pingServer();
        }
    }

    async getByPage(request = {}) {
        /*
      - Hàm này cho phép lấy các bản ghi có trong bảng theo từng trang.
      - Các tham số:
        + page: Trang cần lấy.
        + rowsPerPage: Số bản ghi của mỗi trang.
        + sortBy: Sắp xếp theo các cột (alias của cột).
        + query: Các yêu cầu truy vấn khác, ví dụ: tìm kiếm.
        + columnList: Danh sách các bí danh của các cột cần lấy thông tin.
      -Dữ liệu trả về:
        + data: Danh sách các bản ghi lấy được.
        + pageinfo: Thông tin về trang lấy được. 
    */
        try {
            const {
                page = '',
                rowsPerPage = '',
                sortBy = '',
                query = '',
                columnList = '',
                topList = '',
                filter = '',
            } = request;

            let param = [];

            if (rowsPerPage !== '') param.push(`size=${rowsPerPage}`);
            else param.push(`size=20`);

            if (sortBy !== null && sortBy.length > 0) param.push(`order=${sortBy}`);

            if (query !== '') param.push(`search=${query}`);

            if (filter !== '') param.push(`query=${filter}`);

            if (columnList !== '') param.push(`columnlist=${columnList}`);

            if (topList !== '') param.push(`toplist=${topList}`);

            let strParam = param.join('&');

            if (page !== '') {
                param = `${page}/?${strParam}`;
            } else {
                param = `1/?${strParam}`;
            }

            const responseData = await instance.get(`${this.apiBaseURL}${this.modelName}/page/${param}`, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            let data = normalData(responseData.data.data.records);
            let pageinfo = responseData.data.data.pageinfo;
            let message = responseData.data.data.message;

            return Promise.resolve({
                data,
                pageinfo,
                message,
            });
        } catch (error) {
            pingServer();
        }
    }

    async getById(id, columnlist = '') {
        /*
      - Hàm này cho phép lấy một bản ghi theo mã định danh.
      - Các tham số:
        + id: Mã định danh của bản ghi cần lấy.
      -Dữ liệu trả về:
        + data: Thông tin của bản ghi lấy được.
    */
        try {
            const responseData = await instance.get(
                `${this.apiBaseURL}${this.modelName}/${id}?columnlist=${columnlist}`,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                },
            );

            return Promise.resolve(responseData.data);
        } catch (error) {
            pingServer();
        }
    }

    prepareFormData(request = {}) {
        /*
      - Hàm này cho phép chuẩn bị dữ liệu lấy từ form để gửi lên server thêm/cập nhật.
      - Hàm này sẽ được override ở mỗi lớp con.
    */
    }

    async create(request = {}) {
        /*
      - Hàm này cho phép tạo một bản ghi từ dữ liệu lấy ở form.
      - Các tham số:
        + request: Các nội dung dữ liệu.
      -Dữ liệu trả về:
        + data: Dữ liệu trả về (nếu thành công).
        + error: Thông báo lỗi (nếu có). 
    */
        try {
            let bodyFormData = this.prepareFormData(request);

            const response = await instance.post(`${this.apiBaseURL}${this.modelName}`, bodyFormData, {
                headers: {
                    'Content-Type': `multipart/form-data ; boundary=${bodyFormData._boundary}`,
                },
            });

            if (response.status === 200) {
                return Promise.resolve(response.data);
            }
        } catch (error) {
            pingServer();
            return error;
        }
    }

    async update(request = {}) {
        /*
      - Hàm này cho phép sửa nội dung một bản ghi từ dữ liệu lấy ở form.
      - Các tham số:
        + request: Các nội dung dữ liệu, trong đó có mã định danh của bản ghi cần sửa.
      -Dữ liệu trả về:
        + data: Dữ liệu trả về (nếu thành công).
        + error: Thông báo lỗi (nếu có). 
    */
        try {
            let id = request.id;
            let bodyFormData = this.prepareFormData(request);

            const response = await instance.post(`${this.apiBaseURL}${this.modelName}/${id}`, bodyFormData, {
                headers: {
                    'Content-Type': `multipart/form-data ; boundary=${bodyFormData._boundary}`,
                },
            });

            if (response.status === 200) {
                return Promise.resolve(response.data);
            }
        } catch (error) {
            pingServer();
            return error;
        }
    }

    async delete(id) {
        /*
      - Hàm này cho phép xóa một bản ghi trong bảng.
      - Các tham số:
        + id: Mã định danh của bản ghi cần xóa.
      -Dữ liệu trả về:
        + data: Dữ liệu trả về (nếu thành công).
        + error: Thông báo lỗi (nếu có). 
    */
        try {
            const response = await instance.delete(`${this.apiBaseURL}${this.modelName}/${id}`, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.status === 200) {
                return Promise.resolve(response.data);
            }
        } catch (error) {
            pingServer();
            return error;
        }
    }

    async massdelete(request = {}) {
        /*
      - Hàm này cho phép xóa nhiều bản ghi trong bảng.
      - Các tham số:
        + idlist: Danh sách các mã định danh của các bản ghi cần xóa.
      - Dữ liệu trả về:
        + data: Dữ liệu trả về (nếu thành công).
        + error: Thông báo lỗi (nếu có). 
    */
        try {
            var bodyFormData = new FormData();
            if (request.data != null) {
                bodyFormData.append('idlist', request.data);
            }
            const response = await instance.delete(`${this.apiBaseURL}${this.modelName}/delete`, {
                headers: {
                    'Content-Type': `multipart/form-data; boundary=${bodyFormData._boundary}`,
                },
                data: bodyFormData,
            });

            if (response.status === 200) {
                return Promise.resolve(response.data);
            }
        } catch (error) {
            pingServer();
            return error;
        }
    }

    async copy(request = {}) {
        /*
      - Hàm này cho phép sao chép nhiều bản ghi trong bảng.
      - Các tham số:
        + idlist: Danh sách các mã định danh của các bản ghi cần sao chép.
      - Dữ liệu trả về:
        + data: Dữ liệu trả về (nếu thành công).
        + error: Thông báo lỗi (nếu có). 
    */
        try {
            var bodyFormData = new FormData();
            if (request.data != null) {
                bodyFormData.append('idlist', request.data);
            }
            const response = await instance.post(`${this.apiBaseURL}${this.modelName}/copy`, bodyFormData, {
                headers: {
                    'Content-Type': `multipart/form-data ; boundary=${bodyFormData._boundary}`,
                },
            });

            if (response.status === 200) {
                return Promise.resolve(response.data);
            }
        } catch (error) {
            pingServer();
            return error;
        }
    }

    stream2File = (type, data, filename = undefined) => {
        /*
      - Hàm này cho phép xuất dữ liệu từ stream ra tệp.
      - Các tham số:
        + data: Dữ liệu lấy được từ stream
      - Dữ liệu trả về:
        + Tệp dữ liệu lấy được.
    */

        const contentType = this.fileTypes[type];

        var newBlob = new Blob([data.data], { type: contentType });

        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(newBlob);
            return;
        }

        data = window.URL.createObjectURL(newBlob);
        var link = document.createElement('a');
        document.body.appendChild(link);
        link.href = data;
        link.download = filename ? filename : `${this.modelName}.` + type;
        link.click();
        window.URL.revokeObjectURL(data);
        link.remove();
        return { data: 'ok' };
    };

    async export(request = {}) {
        /*
      - Hàm này cho phép xuất ra tệp một bản ghi trong bảng.
      - Các tham số:
        + idlist: Danh sách các mã định danh của các bản ghi cần xuất ra tệp.
        + type: Định dạng của tệp cần xuất
        + filters: Các điểù kiện lọc dữ liệu
      - Dữ liệu trả về:
        + data: Dữ liệu trả về (nếu thành công).
        + error: Thông báo lỗi (nếu có). 
    */
        try {
            const {
                type,
                pageSize,
                page_width: pageWidth,
                page_height: pageHeight,
                idlist,
                columnlist,
                template,
                filename = undefined,
            } = request;

            let contentType = '';
            if (this.fileTypes[type] !== undefined) {
                contentType = this.fileTypes[type];

                var bodyFormData = new FormData();

                if (columnlist != null) {
                    bodyFormData.append('columnlist', columnlist.join(','));
                }

                if (template != null) {
                    bodyFormData.append('template', JSON.stringify(template));
                }

                let exportQuery = `type=${type}&pageSize=${encodeURIComponent(pageSize)}`;
                if (pageWidth != null && pageWidth !== '') {
                    exportQuery += `&page_width=${encodeURIComponent(pageWidth)}`;
                }
                if (pageHeight != null && pageHeight !== '') {
                    exportQuery += `&page_height=${encodeURIComponent(pageHeight)}`;
                }

                await instance
                    .post(`${this.apiBaseURL}${this.modelName}/export/${request.idlist}/?${exportQuery}`, bodyFormData, {
                        headers: {
                            Accept: 'application/octet-stream',
                            'Content-Type': `multipart/form-data ; boundary=${bodyFormData._boundary}`,
                        },
                        responseType: 'arraybuffer',
                    })
                    .then((data) => {
                        this.stream2File(type, data, filename);
                    });
            }
        } catch (error) {
            pingServer();
            return error;
        }
    }

    async exportRaw(request = {}) {
        /*
      - Hàm này cho phép xuất ra tệp một bản ghi trong bảng.
      - Các tham số:
        + idlist: Danh sách các mã định danh của các bản ghi cần xuất ra tệp.
        + type: Định dạng của tệp cần xuất
        + filters: Các điểù kiện lọc dữ liệu
      - Dữ liệu trả về:
        + data: Dữ liệu trả về (nếu thành công).
        + error: Thông báo lỗi (nếu có). 
    */
        try {
            const { idlist, columnlist, template } = request;

            var bodyFormData = new FormData();

            if (columnlist != null) {
                bodyFormData.append('columnlist', columnlist.join(','));
            }
            if (template != null) {
                bodyFormData.append('template', JSON.stringify(template));
            }

            const response = await instance.post(
                `${this.apiBaseURL}${this.modelName}/export/${idlist}/?type=raw`,
                bodyFormData,
                {
                    headers: {
                        'Content-Type': `multipart/form-data`,
                    },
                },
            );
            return Promise.resolve(response);
        } catch (error) {
            pingServer();
            return error;
        }
    }

    async massExportRaw(request = {}) {
        /*
      - Hàm này cho phép xuất ra tệp nhiều bản ghi trong bảng.
      - Các tham số:
        + idlist: Danh sách các mã định danh của các bản ghi cần xuất ra tệp.
        + type: Định dạng của tệp cần xuất
        + filters: Các điểù kiện lọc dữ liệu
      - Dữ liệu trả về:
        + data: Dữ liệu trả về (nếu thành công).
        + error: Thông báo lỗi (nếu có). 
    */
        try {
            const { idlist, columnlist, template } = request;

            var bodyFormData = new FormData();

            if (columnlist != null) {
                bodyFormData.append('columnlist', columnlist.join(','));
            }
            if (template != null) {
                bodyFormData.append('template', JSON.stringify(template));
            }

            const response = await instance.post(
                `${this.apiBaseURL}${this.modelName}/export?idlist=${idlist}&type=raw`,
                bodyFormData,
                {
                    headers: {
                        'Content-Type': `multipart/form-data`,
                    },
                },
            );
            return Promise.resolve(response);
        } catch (error) {
            pingServer();
            return error;
        }
    }

    async exportAll(request = {}) {
        /*
      - Hàm này cho phép xuất ra tệp các bản ghi trong bảng.
      - Các tham số:
        + idlist: Danh sách các mã định danh của các bản ghi cần xuất ra tệp.
        + type: Định dạng của tệp cần xuất
        + filters: Các điểù kiện lọc dữ liệu
      - Dữ liệu trả về:
        + data: Dữ liệu trả về (nếu thành công).
        + error: Thông báo lỗi (nếu có). 
    */
        try {
            const {
                type,
                pageSize,
                page_width: pageWidth,
                page_height: pageHeight,
                idlist,
                columnlist,
                template,
                sortBy = '',
                filename = undefined,
            } = request;

            if (this.fileTypes[type] !== undefined) {
                var bodyFormData = new FormData();

                if (columnlist != null) {
                    bodyFormData.append('columnlist', columnlist.join(','));
                }

                if (idlist != null) {
                    bodyFormData.append('idlist', idlist);
                }

                if (template != null) {
                    bodyFormData.append('template', JSON.stringify(template));
                }

                if (sortBy !== null && sortBy.length > 0) bodyFormData.append(`order`, sortBy);

                let exportQuery = `type=${type}&pageSize=${encodeURIComponent(pageSize)}`;
                if (pageWidth != null && pageWidth !== '') {
                    exportQuery += `&page_width=${encodeURIComponent(pageWidth)}`;
                }
                if (pageHeight != null && pageHeight !== '') {
                    exportQuery += `&page_height=${encodeURIComponent(pageHeight)}`;
                }

                await instance
                    .post(`${this.apiBaseURL}${this.modelName}/export/?${exportQuery}`, bodyFormData, {
                        headers: {
                            Accept: 'application/octet-stream',
                            'Content-Type': `multipart/form-data ; boundary=${bodyFormData._boundary}`,
                        },
                        responseType: 'arraybuffer',
                    })
                    .then((data) => {
                        this.stream2File(type, data, filename);
                    });
            }
        } catch (error) {
            pingServer();
            return error;
        }
    }

    async import(files) {
        /*
      - Hàm này cho phép nhập dữ liệu cho các bản ghi từ tệp.
      - Các tham số:
        + files: Tệp dữ liệu cần nhập.
      - Dữ liệu trả về:
        + data: Dữ liệu trả về (nếu thành công).
        + error: Thông báo lỗi (nếu có). 
    */

        try {
            var bodyFormData = new FormData();
            if (files != null) {
                bodyFormData.append('attachment', files);
            }
            const response = await instance.post(`${this.apiBaseURL}${this.modelName}/import`, bodyFormData, {
                headers: {
                    'Content-Type': `multipart/form-data ; boundary=${bodyFormData._boundary}`,
                },
            });
            if (response.status === 200) {
                return Promise.resolve(response.data);
            }
        } catch (error) {
            pingServer();
            return error;
        }
    }

    async change_state(id) {
        /*
      - Hàm này dùng để chuyển trạng thái của một bản ghi trong CSDL (active/inactive)
      - Các tham số:
        + id: Mã định danh của bản ghi cần thay đổi trạng thái.
      -Dữ liệu trả về:
        + data: Dữ liệu trả về (nếu thành công).
        + error: Thông báo lỗi (nếu có). 
    */
        try {
            const response = await instance.post(
                `${this.apiBaseURL}${this.modelName}/state/${id}`,
                {},
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },
            );

            if (response.status === 200) {
                return Promise.resolve(response.data);
            }
        } catch (error) {
            pingServer();
            return error;
        }
    }

    async mass_change_state(idlist) {
        /*
      - Hàm này dùng để chuyển trạng thái của nhiều bản ghi trong CSDL (active/inactive)
      - Các tham số:
        + idlist: Danh sách các mã định danh của các bản ghi cần thay đổi trạng thái.
      -Dữ liệu trả về:
        + data: Dữ liệu trả về (nếu thành công).
        + error: Thông báo lỗi (nếu có). 
    */
        try {
            let idlistStr = idlist.join(',') || '';
            const response = await instance.post(
                `${this.apiBaseURL}${this.modelName}/state/?idlist=${idlistStr}`,
                {},
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },
            );

            if (response.status === 200) {
                return Promise.resolve(response.data);
            }
        } catch (error) {
            pingServer();
            return error;
        }
    }

    async download(request = {}) {
        /*
  - Hàm này cho phép tải tệp mẫu để import dữ liệu.
  - Các tham số:       
    + type: Định dạng của tệp cần xuất  
  - Dữ liệu trả về:
    + file: Trả về file với định dạng(nếu thành công).
    + error: Thông báo lỗi (nếu có). 
*/
        try {
            var bodyFormData = new FormData();
            const { type } = request;
            await instance
                .get(`${this.apiBaseURL}${this.modelName}/import/?type=${type}`, {
                    headers: {
                        Accept: 'application/octet-stream',
                        'Content-Type': `multipart/form-data ; boundary=${bodyFormData._boundary}`,
                    },
                    responseType: 'arraybuffer',
                })
                .then((data) => {
                    this.stream2File(type, data);
                });
        } catch (error) {
            pingServer();
            return error;
        }
    }

    async fillIntoSelect(request = {}) {
        /*
    - Hàm này cho phép lấy tất cả dữ liệu phục vụ cho tag <select> 
    - Các tham số:       
        + modelName: tên các bảng cần truy cấn 
    - Dữ liệu trả về:
    + data: Trả về  dữ liệu mảng(nếu thành công).
    + error: Thông báo lỗi (nếu có). 
    */
        try {
            const { columnlist, sortBy = '', query } = request;
            let param = [];
            if (sortBy != null && sortBy.length > 0) {
                param.push(`order=${sortBy}`);
            }
            if (columnlist != undefined && columnlist != null) {
                param.push(`columnlist=${columnlist.join(',')}`);
            }
            if (query != undefined && query != null) {
                param.push(`query=${query}`);
            }
            let strParam = param.join('&');
            let url = `${this.apiBaseURL}${this.modelName}/?${strParam}`;
            const responseData = await instance.get(url, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return responseData.data;
        } catch (error) {
            pingServer();
            return error;
        }
    }

    getByForeignKey = async (foreignKeyName, request = {}) => {
        /*
    Hàm công cụ cho phép lấy dữ liệu từ bảng theo các foreign key (sẽ tạo tại các lớp con).
  */
        try {
            const { columnlist, idlist, toplist, sortBy = '', query = '' } = request;

            let param = [];
            if (idlist !== undefined && idlist.length > 0) {
                param.push(`idlist=${idlist}`);
            }

            if (toplist !== undefined && toplist.length > 0) {
                param.push(`toplist=${toplist}`);
            }

            if (columnlist !== undefined && columnlist != null) {
                param.push(`columnlist=${columnlist.join(',')}`);
            }

            if (query !== undefined && query != null) {
                param.push(`query=${query}`);
            }

            if (sortBy !== undefined && sortBy !== null && sortBy.length > 0) param.push(`order=${sortBy}`);

            let strParam = param.join('&');

            const responseData = await instance.get(
                `${this.apiBaseURL}${this.modelName}/${foreignKeyName}/?${strParam}`,
                {
                    headers: {
                        'Content-Type': `multipart/form-data`,
                    },
                },
            );

            let data = normalData(responseData.data.data);
            return Promise.resolve({
                data,
            });
        } catch (error) {
            pingServer();
            return error;
        }
    };

    getCountByForeignKey = async (foreignKeyName, request = {}) => {
        /*
    Hàm công cụ cho phép lấy dữ liệu từ bảng theo các foreign key (sẽ tạo tại các lớp con).
  */
        try {
            const { idlist } = request;

            let param = [];
            if (idlist !== undefined && idlist !== null && idlist.length > 0) {
                param.push(`idlist=${idlist}`);
            }

            let strParam = param.join('&');

            const articles = await instance.get(
                `${this.apiBaseURL}${this.modelName}/${foreignKeyName}_count/?${strParam}`,
                {
                    headers: {
                        'Content-Type': `multipart/form-data`,
                    },
                },
            );
            let data = normalData(articles.data.data);
            return Promise.resolve({
                data,
            });
        } catch (error) {
            pingServer();
        }
    };

    async create_blank(request = {}) {
        /*
      - Hàm này cho phép tạo một bản ghi từ dữ liệu mặc định trả về ID (phục vụ cho các bảng SO,MF).
      - Các tham số:        
      -Dữ liệu trả về:
        + data: Dữ liệu trả về (nếu thành công).
        + error: Thông báo lỗi (nếu có). 
    */
        try {
            const responseData = await instance.get(`${this.apiBaseURL}${this.modelName}/create_blank`, {
                headers: {
                    'Content-Type': `multipart/form-data`,
                },
            });

            if (responseData.status === 200) {
                return Promise.resolve(responseData.data);
            }
        } catch (error) {
            pingServer();
            return error;
        }
    }
}
export default DataService;
